import axiosInstance from './axiosInstance';

// 新的游戏数据模型接口
export interface Game {
  id: string;
  // 新字段
  companyName?: string;
  coverUrl?: string;
  iconUrl?: string;
  title?: string;
  category?: string;
  desc?: string;
  gameId?: string;
  gameMd5?: string;
  gameUrl?: string;
  tags?: string[];
  categoryName?: string;
  publishedAt?: string;
  mobileMode?: string;
  mobile?: string[];
  instruction?: string;
  languages?: string[];
  isHot?: boolean;
  isTopPerformer?: boolean;
  isRecommended?: boolean;
  goGameUrl?: boolean; // 跳转到游戏URL
  isNewGame?: boolean;
  status?: string; // online, offline
  developerName?: string;
  rating?: number;
  voteCount?: number;
  upvoteCount?: number;
  downvoteCount?: number;
  articleId?: string;
  source?: string;
  webPlatform?: 'Puzzlegame' | 'Rabigame';
  // 兼容旧字段
  name?: string;
  icon?: string;
  cover?: string;
  url?: string;
  is_hot?: boolean;
  is_recommended?: boolean;
  label?: string;
  active?: boolean;
}

export interface GameQuery {
  id?: string | null;
  gameId?: string | null;
  category?: string | null;
  limit?: number;
  skip?: number;
  title?: string | null;
  isHot?: boolean | null;
  isTopPerformer?: boolean | null;
  isRecommended?: boolean | null;
  isNewGame?: boolean | null;
  status?: string | null; // online, offline
  source?: string | null; // AGENT, gamedistribution, ''=无来源
  webPlatform?: 'Puzzlegame' | 'Rabigame' | null;
  // 兼容旧字段
  hot?: boolean | null;
  recommended?: boolean | null;
  name?: string | null;
}

export interface GameListResponse {
  gameList: Game[];
  total: number;
}

export interface CategoryOption {
  id: string;
  name: string;
  order?: number;
}

export interface ResourceUploadResponse {
  name: string;
}

export interface BulkUploadResult {
  total: number;
  successCount: number;
  failedCount: number;
  success: Array<{
    index?: number;
    gameId?: string;
    title?: string;
    id: string;
    relatedCount?: number;
  }>;
  failed: Array<{
    index?: number;
    gameId?: string;
    title?: string;
    error: string;
  }>;
}

export interface BulkReplaceGameUrlDomainResult {
  total: number;
  updatedCount: number;
  skippedCount: number;
  failedCount: number;
  updated: Array<{
    id: string;
    gameId?: string;
    oldUrl: string;
    newUrl: string;
  }>;
  skipped: Array<{
    id: string;
    gameId?: string;
    reason: string;
  }>;
  failed: Array<{
    id: string;
    error: string;
  }>;
}

export class GameApi {
  static async getGames(query: GameQuery): Promise<GameListResponse> {
    // 处理布尔值参数，转换为字符串
    const params: any = { ...query };
    if (params.isHot !== null && params.isHot !== undefined) {
      params.isHot = String(params.isHot);
    }
    if (params.isTopPerformer !== null && params.isTopPerformer !== undefined) {
      params.isTopPerformer = String(params.isTopPerformer);
    }
    if (params.isRecommended !== null && params.isRecommended !== undefined) {
      params.isRecommended = String(params.isRecommended);
    }
    if (params.isNewGame !== null && params.isNewGame !== undefined) {
      params.isNewGame = String(params.isNewGame);
    }
    // status 为空字符串时也需要传递（代表未上线）
    if (params.status !== null && params.status !== undefined) {
      params.status = params.status; // 保持原值，包括空字符串
    }
    // source 为空字符串时也需要传递（代表无来源）
    if (params.source !== null && params.source !== undefined) {
      params.source = params.source;
    }
    const response = await axiosInstance.get('/cms/v1/game', { params });
    return response.data.data;
  }

  static async importGames(): Promise<void> {
    await axiosInstance.get('/cms/v1/import/game');
  }

  static async createGame(game: Partial<Game>): Promise<Game> {
    const response = await axiosInstance.post('/cms/v1/game', game);
    return response.data.data;
  }

  static async updateGame(id: string, game: Partial<Game>): Promise<Game> {
    const response = await axiosInstance.put(`/cms/v1/game/${id}`, game);
    return response.data.data;
  }

  static async deleteGame(id: string): Promise<void> {
    await axiosInstance.delete(`/cms/v1/game/${id}`);
  }

  static async sortGames(ids: string[]): Promise<void> {
    await axiosInstance.post('/cms/v1/sort/game', { sort: ids });
  }

  static async topGame(id: string): Promise<Game> {
    const response = await axiosInstance.put(`/cms/v1/game/${id}`, { action: 'top' });
    return response.data.data;
  }

  static async bulkReplaceGameUrlDomain(
    ids: string[],
    oldDomain: string,
    newDomain: string
  ): Promise<BulkReplaceGameUrlDomainResult> {
    const response = await axiosInstance.post('/cms/v1/game/url-domain/bulk', {
      ids,
      oldDomain,
      newDomain
    });
    return response.data.data;
  }

  static async bulkUpdateGames(
    ids: string[],
    updates: { status?: string; category?: string; webPlatform?: 'Puzzlegame' | 'Rabigame' }
  ): Promise<BulkUploadResult> {
    const response = await axiosInstance.put('/cms/v1/game/bulk', {
      ids,
      ...updates
    });
    return response.data.data;
  }

  /**
   * 批量上传游戏
   * @param games 游戏数组，格式参考 hrefs-test.json
   */
  static async bulkCreateGames(games: Partial<Game>[]): Promise<BulkUploadResult> {
    // 手动序列化为 JSON 字符串，确保数据格式正确
    const response = await axiosInstance.post('/cms/v1/game/bulk', JSON.stringify(games), {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data.data;
  }

  /**
   * 批量上传游戏（通过文件上传，支持多个 JSON 文件和 ZIP 压缩包）
   * @param formData FormData 对象，包含要上传的文件
   */
  static async bulkCreateGamesWithFiles(formData: FormData): Promise<BulkUploadResult> {
    const response = await axiosInstance.post('/cms/v1/game/bulk', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data.data;
  }

  /**
   * 批量上传游戏（使用对象格式）
   * @param games 游戏数组
   */
  static async bulkCreateGamesWithWrapper(games: Partial<Game>[]): Promise<BulkUploadResult> {
    const response = await axiosInstance.post('/cms/v1/game/bulk', { games });
    return response.data.data;
  }

  static async uploadResource(file: File, prefix: string): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('prefix', prefix);

    const response = await axiosInstance.post<{ data: ResourceUploadResponse }>(
      '/cms/v1/resource',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );

    return response.data.data.name;
  }

  static async getCategories(): Promise<CategoryOption[]> {
    const response = await axiosInstance.get('/cms/v1/category?include_order=true');
    return response.data.data.categoryList;
  }

  /**
   * 批量上传游戏相关关系
   * @param relateMap 游戏相关关系映射，格式: {gameId: [{"id": "...", "score": 0.123, "gameMd5": "..."}, ...]}
   */
  static async bulkUploadRelateGames(relateMap: Record<string, Array<{
    id: string;
    score: number;
    gameMd5?: string;
  }>>): Promise<BulkUploadResult> {
    // 手动序列化为 JSON 字符串，确保数据格式正确
    const response = await axiosInstance.post('/cms/v1/game/relate/bulk', JSON.stringify(relateMap), {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data.data;
  }

  /**
   * 获取游戏的相关游戏列表
   * @param gameId 游戏ID
   */
  static async getRelateGames(gameId: string): Promise<Array<{
    id: string;
    score: number;
    gameMd5?: string;
  }>> {
    const response = await axiosInstance.get(`/cms/v1/game/relate/${gameId}`);
    return response.data.data || [];
  }

  /**
   * 删除游戏的相关关系
   * @param gameId 游戏ID
   */
  static async deleteRelateGames(gameId: string): Promise<void> {
    await axiosInstance.delete(`/cms/v1/game/relate/${gameId}`);
  }

  /**
   * 批量上传热门游戏
   * @param games 游戏数组
   */
  static async bulkUploadHotGames(games: Partial<Game>[]): Promise<BulkUploadResult> {
    const response = await axiosInstance.post('/cms/v1/game/hot/bulk', JSON.stringify(games), {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data.data;
  }

  /**
   * 批量上传最受欢迎游戏
   * @param games 游戏数组
   */
  static async bulkUploadTopPerformerGames(games: Partial<Game>[]): Promise<BulkUploadResult> {
    const response = await axiosInstance.post('/cms/v1/game/top-performer/bulk', JSON.stringify(games), {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data.data;
  }

  /**
   * 批量删除游戏
   * @param ids 游戏ID数组
   */
  static async bulkDeleteGames(ids: string[]): Promise<BulkUploadResult> {
    const response = await axiosInstance.delete('/cms/v1/game/bulk', {
      data: JSON.stringify(ids),
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data.data;
  }
}